import { CircleUserRound } from "lucide-react";
import { type JSX } from "react";
import { cn } from "./lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "./ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

export interface ProfileMenuProps {
  collapsed: boolean;
  identityEmail: string | null;
  tenantName: string | null;
  authEnabled: boolean;
  canChangeTenant: boolean;
  onChangeTenant: () => void;
  onSignOut: () => void;
}

const profileMenuIcons = {
  changeTenant: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLWJ1aWxkaW5nMiBsdWNpZGUtYnVpbGRpbmctMiIgYXJpYS1oaWRkZW49InRydWUiPjxwYXRoIGQ9Ik0xMCAxMmg0Ij48L3BhdGg+PHBhdGggZD0iTTEwIDhoNCI+PC9wYXRoPjxwYXRoIGQ9Ik0xNCAyMXYtM2EyIDIgMCAwIDAtNCAwdjMiPjwvcGF0aD48cGF0aCBkPSJNNiAxMEg0YTIgMiAwIDAgMC0yIDJ2N2EyIDIgMCAwIDAgMiAyaDE2YTIgMiAwIDAgMCAyLTJWOWEyIDIgMCAwIDAtMi0yaC0yIj48L3BhdGg+PHBhdGggZD0iTTYgMjFWNWEyIDIgMCAwIDEgMi0yaDhhMiAyIDAgMCAxIDIgMnYxNiI+PC9wYXRoPjwvc3ZnPg==",
  settings: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLXNldHRpbmdzIiBhcmlhLWhpZGRlbj0idHJ1ZSI+PHBhdGggZD0iTTkuNjcxIDQuMTM2YTIuMzQgMi4zNCAwIDAgMSA0LjY1OSAwIDIuMzQgMi4zNCAwIDAgMCAzLjMxOSAxLjkxNSAyLjM0IDIuMzQgMCAwIDEgMi4zMyA0LjAzMyAyLjM0IDIuMzQgMCAwIDAgMCAzLjgzMSAyLjM0IDIuMzQgMCAwIDEtMi4zMyA0LjAzMyAyLjM0IDIuMzQgMCAwIDAtMy4zMTkgMS45MTUgMi4zNCAyLjM0IDAgMCAxLTQuNjU5IDAgMi4zNCAyLjM0IDAgMCAwLTMuMzItMS45MTUgMi4zNCAyLjM0IDAgMCAxLTIuMzMtNC4wMzMgMi4zNCAyLjM0IDAgMCAwIDAtMy44MzFBMi4zNCAyLjM0IDAgMCAxIDYuMzUgNi4wNTFhMi4zNCAyLjM0IDAgMCAwIDMuMzE5LTEuOTE1Ij48L3BhdGg+PGNpcmNsZSBjeD0iMTIiIGN5PSIxMiIgcj0iMyI+PC9jaXJjbGU+PC9zdmc+",
  signOut: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLWxvZy1vdXQiIGFyaWEtaGlkZGVuPSJ0cnVlIj48cGF0aCBkPSJtMTYgMTcgNS01LTUtNSI+PC9wYXRoPjxwYXRoIGQ9Ik0yMSAxMkg5Ij48L3BhdGg+PHBhdGggZD0iTTkgMjFINWEyIDIgMCAwIDEtMi0yVjVhMiAyIDAgMCAxIDItMmg0Ij48L3BhdGg+PC9zdmc+"
} as const;

export function ProfileMenu({
  collapsed,
  identityEmail,
  tenantName,
  authEnabled,
  canChangeTenant,
  onChangeTenant,
  onSignOut
}: ProfileMenuProps): JSX.Element {
  const trigger = (
    <button
      type="button"
      aria-label="Account"
      data-platform-profile-menu=""
      className={cn(
        "flex items-center gap-2 rounded-lg font-body text-sm text-sidebar-text-muted transition-[background-color,color,transform] duration-150 ease-out hover:scale-[1.01] active:scale-[.99] motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100",
        "bg-sidebar-surface-hover hover:bg-nav-active hover:text-sidebar-text",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "focus-visible:ring-offset-2 focus-visible:ring-offset-shell-canvas",
        collapsed ? "size-9 justify-center" : "h-9 w-full px-3"
      )}
    >
      <CircleUserRound className="size-5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
      {!collapsed && <span className="truncate">Account</span>}
    </button>
  );

  return (
    <DropdownMenu>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">Account</TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      )}

      <DropdownMenuContent align="start" side="top" className="min-w-56">
        {authEnabled && identityEmail ? (
          <>
            <DropdownMenuLabel className="font-normal">
              <span className="block truncate text-text">{identityEmail}</span>
              {tenantName ? (
                <span className="block truncate text-text-muted" data-platform-account-tenant="">
                  {tenantName}
                </span>
              ) : null}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        ) : null}

        {canChangeTenant ? (
          <DropdownMenuItem onSelect={onChangeTenant} data-platform-change-tenant="">
            <img src={profileMenuIcons.changeTenant} alt="" aria-hidden="true" width={16} height={16} />
            <span>Change tenant</span>
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuItem disabled>
          <img src={profileMenuIcons.settings} alt="" aria-hidden="true" width={16} height={16} />
          <span>Settings</span>
        </DropdownMenuItem>

        {authEnabled ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onSignOut}>
              <img src={profileMenuIcons.signOut} alt="" aria-hidden="true" width={16} height={16} />
              <span>Sign out</span>
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
